import { useAppDispatch } from 'app/hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { useEditCompanyOwnerMutation, useGetCompanyOwnerQuery } from '../services';
import { useForm } from 'hooks/useForm';
import { CompanyOwner, CompanyOwnerDto } from '../types/dtos';
import { COMPANY_OWNER_EDIT_FIELDS_SCHEMA, COMPANY_OWNER_INITIAL_FIELDS } from '../constants/company-owner-fields';
import { FormEvent, useCallback, useEffect, useMemo } from 'react';
import { setErrorMessage, setSuccessMessage } from 'store/toast-slice';
import { ServerErrorType } from 'types/server-responses';
import { trimValues } from 'utils/object-helpers';

const useController = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [editCompanyOwner, { isLoading: isEditCompanyOwnerLoading }] = useEditCompanyOwnerMutation();
    const { data: companyOwner } = useGetCompanyOwnerQuery(id as string);

    const { formData, setInitialFormData, handleChange, handleSubmit, errors, isDirty, changedValues } =
        useForm<CompanyOwner>({
            validationSchema: COMPANY_OWNER_EDIT_FIELDS_SCHEMA,
            options: { trackChangedValues: true },
        });

    const { firstName, lastName } = useMemo(() => {
        const fullNameParts = companyOwner?.fullName?.trim().split(' ') ?? [];
        return {
            firstName: fullNameParts[0] ?? '',
            lastName: fullNameParts.slice(1).join(' ') ?? '',
        };
    }, [companyOwner?.fullName]);

    useEffect(() => {
        if (!companyOwner) return;

        const editCompanyOwner = {
            ...companyOwner,
            firstName,
            lastName,
            email: companyOwner?.account?.email ?? '',
        };

        if (editCompanyOwner) setInitialFormData({ ...COMPANY_OWNER_INITIAL_FIELDS, ...editCompanyOwner });
    }, [setInitialFormData, companyOwner, firstName, lastName]);

    const handleSuccess = useCallback(() => {
        dispatch(setSuccessMessage({ key: 'company_owner.COMPANY_OWNER_EDITED' }));
    }, [dispatch]);

    const handleErrors = useCallback(
        (error: ServerErrorType) => {
            const code = error.data?.errorCode;
            dispatch(setErrorMessage({ key: code }));
        },
        [dispatch],
    );

    const getPayload = useCallback(() => {
        let data = { ...changedValues };
        if (data.email && data.password) {
            data = { ...data, account: { email: data.email, password: data.password } } as CompanyOwnerDto;
            delete data.email;
            delete data.password;
        } else if (data.email) {
            data = { ...data, account: { email: data.email } } as CompanyOwnerDto;
            delete data.email;
        } else if (data.password) {
            data = { ...data, account: { password: data.password } } as CompanyOwnerDto;
            delete data.password;
        }

        if (data.firstName && data.lastName) {
            data.fullName = `${data.firstName} ${data.lastName}`;
            delete data.lastName;
            delete data.firstName;
        } else if (data.firstName) {
            data.fullName = `${data.firstName} ${lastName}`;
            delete data.firstName;
        } else if (data.lastName) {
            data.fullName = `${firstName} ${data.lastName}`;
            delete data.lastName;
        }

        return trimValues(data) as CompanyOwnerDto;
    }, [changedValues, firstName, lastName]);

    const onSubmit = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            handleSubmit(async () => {
                try {
                    const payload = getPayload();

                    await editCompanyOwner({
                        id: id as string,
                        payload,
                    }).unwrap();
                    handleSuccess();
                    navigate(-1);
                } catch (error) {
                    handleErrors(error as ServerErrorType);
                }
            });
        },
        [editCompanyOwner, getPayload, handleErrors, handleSubmit, handleSuccess, id, navigate],
    );

    const onCancel = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    return {
        formData: formData as CompanyOwner,
        onChange: handleChange,
        onSubmit,
        onCancel,
        isLoading: formData === undefined,
        isEditCompanyOwnerLoading,
        errors,
        dataChanged: isDirty,
    };
};

export default useController;
